/**
 * Parking History Page
 * Shows parking registration logs
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Alert,
  Pagination,
  Modal,
} from 'react-bootstrap';
import {
  getParkingLogs,
} from '../services/api';
import {
  formatDate,
  getStatusVariant,
  parseApiResponse,
  parseApiRequest,
  extractPermitHash,
  getPermitLink,
} from '../utils/helpers';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ResponsiveTable from '../components/ResponsiveTable';

const ParkingHistory = () => {
  const [parkingLogs, setParkingLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(8);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [logsData, configData] = await Promise.all([
        getParkingLogs(null, 90), // Last 90 days
        fetch('/api/config').then(res => res.json()),
      ]);
      setParkingLogs(logsData);
      setDebugMode(configData.debugMode);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleShowResponse = (apiResponse) => {
    setSelectedResponse(apiResponse);
    setShowResponseModal(true);
  };

  const handleCloseResponseModal = () => {
    setShowResponseModal(false);
    setSelectedResponse(null);
  };

  const handleShowRequest = (apiRequest) => {
    setSelectedRequest(apiRequest);
    setShowRequestModal(true);
  };

  const handleCloseRequestModal = () => {
    setShowRequestModal(false);
    setSelectedRequest(null);
  };

  if (loading) {
    return <LoadingSpinner message="Loading parking history..." />;
  }

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h2>Parking Registration History</h2>
          <p className="text-muted">
            View all parking registration logs from the last 90 days.
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          Error: {error}
        </Alert>
      )}

      {/* Parking Logs */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <span>Registration History</span>
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted">
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Auto-refreshes every 30s
                  </small>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={fetchData}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Pagination Controls (Top) */}
              {parkingLogs.length > logsPerPage && (
                <div className="d-flex justify-content-center mb-3">
                  <Pagination>
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    />

                    {(() => {
                      const totalPages = Math.ceil(parkingLogs.length / logsPerPage);
                      const pages = [];
                      const maxPagesToShow = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

                      if (endPage - startPage + 1 < maxPagesToShow) {
                        startPage = Math.max(1, endPage - maxPagesToShow + 1);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Pagination.Item
                            key={i}
                            active={i === currentPage}
                            onClick={() => setCurrentPage(i)}
                          >
                            {i}
                          </Pagination.Item>
                        );
                      }

                      return pages;
                    })()}

                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(prev + 1, Math.ceil(parkingLogs.length / logsPerPage))
                        )
                      }
                      disabled={currentPage === Math.ceil(parkingLogs.length / logsPerPage)}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(Math.ceil(parkingLogs.length / logsPerPage))}
                      disabled={currentPage === Math.ceil(parkingLogs.length / logsPerPage)}
                    />
                  </Pagination>
                </div>
              )}

              <ResponsiveTable
                data={parkingLogs.slice(
                  (currentPage - 1) * logsPerPage,
                  currentPage * logsPerPage
                )}
                columns={(() => {
                  const cols = [
                    {
                      key: 'date',
                      label: 'Date',
                      render: (value, log) => (
                        <>
                          {formatDate(log.created_at)}
                          <br />
                          <small className="text-muted">
                            {formatDate(log.registered_date, 'EEEE')}
                          </small>
                        </>
                      ),
                    },
                    { key: 'name', label: 'User' },
                    {
                      key: 'duration_hours',
                      label: 'Duration',
                      render: (value, log) => `${log.duration_hours} hours`,
                    },
                    {
                      key: 'status',
                      label: 'Status',
                      render: (value, log) => (
                        <Badge bg={getStatusVariant(log.status)}>
                          {log.status}
                        </Badge>
                      ),
                    },
                    {
                      key: 'permit',
                      label: 'Permit Link',
                      render: (value, log) => {
                        const permitHash = extractPermitHash(log.api_response);
                        const permitLink = getPermitLink(permitHash);
                        return permitLink ? (
                          <a href={permitLink} target="_blank" rel="noopener noreferrer">
                            <i className="bi bi-link-45deg me-1"></i>
                            View Permit
                          </a>
                        ) : (
                          <span className="text-muted">N/A</span>
                        );
                      },
                    },
                    {
                      key: 'response',
                      label: 'Response',
                      render: (value, log) => {
                        const apiResp = parseApiResponse(log.api_response);
                        let displayStatus = null;
                        if (Array.isArray(apiResp) && apiResp.length > 0) {
                          displayStatus = apiResp[0];
                        } else if (apiResp && !Array.isArray(apiResp)) {
                          displayStatus = apiResp;
                        }
                        return displayStatus ? (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleShowResponse(log.api_response)}
                            className="p-0"
                          >
                            <small>
                              <i className="bi bi-file-earmark-code me-1"></i>
                              HTTP {displayStatus.status}: {
                                // For failed requests, show data.message if available
                                displayStatus.status >= 400 && displayStatus.data?.message
                                  ? displayStatus.data.message
                                  : displayStatus.statusText
                              }
                              {Array.isArray(apiResp) && apiResp.length > 1 && ` (+${apiResp.length - 1} more)`}
                            </small>
                          </Button>
                        ) : (
                          <span className="text-muted">N/A</span>
                        );
                      },
                    },
                  ];

                  if (debugMode) {
                    cols.push({
                      key: 'request',
                      label: 'Request (Debug)',
                      render: (value, log) => {
                        if (!log.api_request) {
                          return <span className="text-muted">N/A</span>;
                        }
                        const requests = parseApiRequest(log.api_request);
                        const requestCount = requests && requests.length > 0 ? requests.length : 1;
                        return (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleShowRequest(log.api_request)}
                            className="p-0"
                          >
                            <small>
                              <i className="bi bi-terminal me-1"></i>
                              View CURL ({requestCount})
                            </small>
                          </Button>
                        );
                      },
                    });
                  }

                  return cols;
                })()}
                renderRow={(log) => {
                  const apiResp = parseApiResponse(log.api_response);
                  const permitHash = extractPermitHash(log.api_response);
                  const permitLink = getPermitLink(permitHash);
                  let displayStatus = null;
                  if (Array.isArray(apiResp) && apiResp.length > 0) {
                    displayStatus = apiResp[0];
                  } else if (apiResp && !Array.isArray(apiResp)) {
                    displayStatus = apiResp;
                  }

                  return (
                    <>
                      <div className="table-card-field" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                        <div className="table-card-primary" style={{ marginBottom: '0' }}>
                          {formatDate(log.created_at)}
                        </div>
                        <div className="table-card-secondary" style={{ marginBottom: '0', textAlign: 'right' }}>
                          {formatDate(log.registered_date, 'EEEE')}
                        </div>
                      </div>
                      <div className="table-card-divider"></div>
                      <div className="table-card-field">
                        <strong>User:</strong> {log.name}
                      </div>
                      <div className="table-card-field">
                        <strong>Duration:</strong> {log.duration_hours} hours
                      </div>
                      <div className="table-card-field">
                        <strong>Status:</strong>{' '}
                        <Badge bg={getStatusVariant(log.status)}>
                          {log.status}
                        </Badge>
                      </div>
                      {permitLink && log.status === 'SUCCESS' && (
                        <div className="table-card-field">
                          <a href={permitLink} target="_blank" rel="noopener noreferrer">
                            <i className="bi bi-link-45deg me-1"></i>
                            View Permit
                          </a>
                        </div>
                      )}
                    </>
                  );
                }}
                emptyState={<EmptyState icon="p-square" message="No parking logs yet" />}
                hover={true}
              />

              {/* Pagination Controls (Bottom) */}
              {parkingLogs.length > logsPerPage && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    />

                    {(() => {
                      const totalPages = Math.ceil(parkingLogs.length / logsPerPage);
                      const pages = [];
                      const maxPagesToShow = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

                      if (endPage - startPage + 1 < maxPagesToShow) {
                        startPage = Math.max(1, endPage - maxPagesToShow + 1);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Pagination.Item
                            key={i}
                            active={i === currentPage}
                            onClick={() => setCurrentPage(i)}
                          >
                            {i}
                          </Pagination.Item>
                        );
                      }

                      return pages;
                    })()}

                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(prev + 1, Math.ceil(parkingLogs.length / logsPerPage))
                        )
                      }
                      disabled={currentPage === Math.ceil(parkingLogs.length / logsPerPage)}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(Math.ceil(parkingLogs.length / logsPerPage))}
                      disabled={currentPage === Math.ceil(parkingLogs.length / logsPerPage)}
                    />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* API Response JSON Modal */}
      <Modal show={showResponseModal} onHide={handleCloseResponseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-earmark-code me-2"></i>
            API Response JSON
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre style={{
            backgroundColor: '#f5f5f5',
            padding: '15px',
            borderRadius: '5px',
            maxHeight: '500px',
            overflow: 'auto',
            fontSize: '12px'
          }}>
            {selectedResponse ? JSON.stringify(JSON.parse(selectedResponse), null, 2) : 'No response data'}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              navigator.clipboard.writeText(selectedResponse || '');
              alert('JSON copied to clipboard!');
            }}
          >
            <i className="bi bi-clipboard me-1"></i>
            Copy to Clipboard
          </Button>
          <Button variant="primary" onClick={handleCloseResponseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* API Request CURL Modal */}
      <Modal show={showRequestModal} onHide={handleCloseRequestModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-terminal me-2"></i>
            API Request CURL Commands
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            if (!selectedRequest) {
              return <p>No request data</p>;
            }

            // Try to parse as new array format
            const requests = parseApiRequest(selectedRequest);

            if (requests && requests.length > 0) {
              // New format: array of requests
              return requests.map((req, index) => (
                <div key={index} style={{ marginBottom: index < requests.length - 1 ? '20px' : '0' }}>
                  <h6 style={{ marginBottom: '10px' }}>
                    <Badge bg="info">Request {req.idx}</Badge>{' '}
                    <span style={{ fontWeight: 'normal' }}>{req.description}</span>
                  </h6>
                  <pre style={{
                    backgroundColor: '#f5f5f5',
                    padding: '15px',
                    borderRadius: '5px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    fontSize: '12px',
                    marginBottom: '0'
                  }}>
                    {req.request}
                  </pre>
                </div>
              ));
            } else {
              // Old format: plain string
              return (
                <pre style={{
                  backgroundColor: '#f5f5f5',
                  padding: '15px',
                  borderRadius: '5px',
                  maxHeight: '500px',
                  overflow: 'auto',
                  fontSize: '12px'
                }}>
                  {selectedRequest}
                </pre>
              );
            }
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              // Copy all CURL commands
              const requests = parseApiRequest(selectedRequest);
              let textToCopy = '';

              if (requests && requests.length > 0) {
                textToCopy = requests
                  .map(r => `# ${r.description} (Request ${r.idx})\n${r.request}`)
                  .join('\n\n');
              } else {
                textToCopy = selectedRequest;
              }

              navigator.clipboard.writeText(textToCopy || '');
              alert('CURL command(s) copied to clipboard!');
            }}
          >
            <i className="bi bi-clipboard me-1"></i>
            Copy to Clipboard
          </Button>
          <Button variant="primary" onClick={handleCloseRequestModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ParkingHistory;
